// src/pages/agent/AgentDashboard.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Modal,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import DoneIcon from "@mui/icons-material/Done";
import FollowUpBell from "../FollowUpBell";
import FollowUpCalendar from "../FollowUpCalendar";
import AgentAnalytics from "./AgentAnalytics";

// Dummy Leads (later can be API)
const initialLeads = [
  {
    id: 1,
    name: "Rahul Sharma",
    phone: "9876543210",
    product: "Hydro Machine",
    status: "Active",
    date: "2025-01-21",
    notes: "Interested in discount",
    followUp: "2025-01-25",
  },
  {
    id: 2,
    name: "Amit Kumar",
    phone: "9000011122",
    product: "Grass Cutter",
    status: "Closed",
    date: "2025-01-20",
    notes: "",
    followUp: "",
  },
];

export default function AgentDashboard() {
  const [leads, setLeads] = useState(initialLeads);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  // Lead Modal State
  const [open, setOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const handleOpenModal = (lead) => {
    setSelectedLead({ ...lead });
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedLead(null);
  };

  // Update lead inside modal
  const updateLeadField = (key, value) => {
    setSelectedLead((prev) => ({ ...prev, [key]: value }));
  };

  const saveLeadChanges = () => {
    setLeads((prev) =>
      prev.map((l) => (l.id === selectedLead.id ? selectedLead : l))
    );
    handleCloseModal();
  };

  // Convert Lead → Order
  const convertLeadToOrder = () => {
    const orders = JSON.parse(localStorage.getItem("orders") || "[]");

    orders.push({
      id: `order_${Date.now()}`,
      customer: selectedLead.name,
      phone: selectedLead.phone,
      product: selectedLead.product,
      date: new Date().toISOString().split("T")[0],
      status: "Pending",
    });

    localStorage.setItem("orders", JSON.stringify(orders));

    alert("Lead converted to order!");
    handleCloseModal();
  };

  // Delete Lead
  const deleteLead = (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      setLeads((prev) => prev.filter((l) => l.id !== id));
    }
  };

  // Toggle lead status
  const toggleStatus = (lead) => {
    setLeads((prev) =>
      prev.map((l) =>
        l.id === lead.id
          ? { ...l, status: l.status === "Active" ? "Closed" : "Active" }
          : l
      )
    );
  };

  // Search + Filter
  const filteredLeads = leads.filter((lead) => {
    const statusMatch = filter === "All" ? true : lead.status === filter;
    const searchMatch =
      lead.name.toLowerCase().includes(search.toLowerCase()) ||
      lead.phone.includes(search) ||
      lead.product.toLowerCase().includes(search.toLowerCase());

    return statusMatch && searchMatch;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* <Typography variant="h4" fontWeight={700}>
        Agent Dashboard
      </Typography> */}
<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
  <Typography variant="h4" fontWeight={700}>Agent Dashboard</Typography>
  <FollowUpBell leads={leads} />
</Box>

      {/* Lead Stats */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {[
          { label: "Total Leads", value: leads.length, icon: <LeaderboardIcon /> },
          {
            label: "Active Leads",
            value: leads.filter((l) => l.status === "Active").length,
            icon: <AssignmentIcon />,
          },
          {
            label: "Closed Leads",
            value: leads.filter((l) => l.status === "Closed").length,
            icon: <DoneIcon />,
          },
        ].map((stat) => (
          <Grid item xs={12} md={4} key={stat.label}>
            <Paper sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
              <Box sx={{ fontSize: 40 }}>{stat.icon}</Box>
              <Typography variant="h6">{stat.label}</Typography>
              <Typography variant="h5" fontWeight={700}>
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* FILTERS + SEARCH */}
      <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {["All", "Active", "Closed"].map((f) => (
              <Chip
                key={f}
                label={f}
                color={filter === f ? "primary" : "default"}
                onClick={() => setFilter(f)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>

          <TextField
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ width: 250 }}
          />
        </Box>

        {/* Leads Table */}
        <Table sx={{ mt: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Phone</b></TableCell>
              <TableCell><b>Product</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredLeads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell>{lead.name}</TableCell>
                <TableCell>{lead.phone}</TableCell>
                <TableCell>{lead.product}</TableCell>

                <TableCell>
                  <Chip
                    label={lead.status}
                    color={lead.status === "Active" ? "warning" : "success"}
                    size="small"
                  />
                </TableCell>

                <TableCell>{lead.date}</TableCell>

                <TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => handleOpenModal(lead)}
                  >
                    View
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => toggleStatus(lead)}
                  >
                    Toggle Status
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => deleteLead(lead.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* LEAD DETAILS MODAL */}
      <Modal open={open} onClose={handleCloseModal}>
        <Box
          sx={{
            width: 400,
            p: 3,
            background: "#fff",
            borderRadius: 3,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Lead Details
          </Typography>

          <TextField
            fullWidth
            label="Name"
            value={selectedLead?.name || ""}
            sx={{ mt: 2 }}
            onChange={(e) => updateLeadField("name", e.target.value)}
          />

          <TextField
            fullWidth
            label="Phone"
            value={selectedLead?.phone || ""}
            sx={{ mt: 2 }}
            onChange={(e) => updateLeadField("phone", e.target.value)}
          />

          <TextField
            fullWidth
            label="Product"
            value={selectedLead?.product || ""}
            sx={{ mt: 2 }}
            onChange={(e) => updateLeadField("product", e.target.value)}
          />

          <TextField
            fullWidth
            label="Notes"
            value={selectedLead?.notes || ""}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            onChange={(e) => updateLeadField("notes", e.target.value)}
          />

          <TextField
            fullWidth
            type="date"
            label="Follow-up Date"
            InputLabelProps={{ shrink: true }}
            value={selectedLead?.followUp || ""}
            sx={{ mt: 2 }}
            onChange={(e) => updateLeadField("followUp", e.target.value)}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button variant="contained" onClick={saveLeadChanges}>
              Save
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={convertLeadToOrder}
            >
              Convert to Order
            </Button>

            <Button variant="outlined" onClick={handleCloseModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* <FollowUpCalendar leads={leads} /> */}
<AgentAnalytics leads={leads} />

    </Box>
  );
}
